/**
 * CalculatriceEmployeur — Calculatrice de coût employeur Québec 2026
 * - Taux fixes par loi : affichés avec cadenas, non modifiables
 * - Taux configurables : modifiables par l'employeur
 * - Assurances collectives : split 50/50 employeur/employé
 * - Temps et demi (>40h) calculé automatiquement
 */
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calculator, ChevronDown, ChevronUp, Lock, Unlock, TrendingUp, Clock, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// ── Taux par défaut (utilisés si la DB ne répond pas) ───────────────────────
const TAUX_HORS_CCQ_DEFAULT = {
  rrq_taux: 5.30,
  ae_taux: 1.82,
  rqap_taux: 0.602,
  fss_taux: 4.26,
  cnesst_taux: 5.22,
  taux_vacances: 6.0,
  assurances_total_par_periode: 0,  // Total (employeur + employé) / période
  heures_par_periode: 80,
};

const TAUX_CCQ_EXTRA_DEFAULT = {
  ccq_avantages_sociaux: 3.11,
  ccq_retraite_taux: 8.0,
  ccq_fnc_taux: 0.75,
  ccq_formation_taux: 1.0,
};

// Quels taux sont fixes par la loi (non modifiables par l'employeur)
const TAUX_FIXES_PAR_LOI = ['rrq_taux', 'ae_taux', 'rqap_taux'];

/**
 * Calcule le coût employeur complet avec split assurances 50/50.
 */
export function calculerCoutEmployeur({ tauxBase, taux, estCCQ, ccqExtra, primeHoraire = 0, bonusHoraire = 0, typePaiement = 'standard' }) {
  const assurances_total = taux.assurances_total_par_periode || 0;
  const assurances_employeur_h = (assurances_total * 0.5) / Math.max(taux.heures_par_periode || 80, 1);
  const assurances_employe_h = (assurances_total * 0.5) / Math.max(taux.heures_par_periode || 80, 1);

  if (typePaiement === 'simple') {
    const cnesst = tauxBase * (taux.cnesst_taux / 100);
    const vacances = tauxBase * (taux.taux_vacances / 100);
    const total = tauxBase + vacances + cnesst + assurances_employeur_h + primeHoraire + bonusHoraire;
    const totalOT = tauxBase * 1.5 + vacances * 1.5 + cnesst * 1.5 + assurances_employeur_h + primeHoraire + bonusHoraire;
    return {
      normal: total, ot: totalOT,
      assurances_employe_h,
      breakdown: { tauxBase, vacances, cnesst, assurances_employeur_h, assurances_employe_h, primeHoraire, bonusHoraire, note: 'Simple (sans déductions sociales)' },
    };
  }

  const vacances = tauxBase * (taux.taux_vacances / 100);
  const salaireAvecVacances = tauxBase + vacances;
  const rrq = salaireAvecVacances * (taux.rrq_taux / 100);
  const ae = salaireAvecVacances * (taux.ae_taux / 100);
  const rqap = salaireAvecVacances * (taux.rqap_taux / 100);
  const fss = salaireAvecVacances * (taux.fss_taux / 100);
  const cnesst = salaireAvecVacances * (taux.cnesst_taux / 100);

  const ccqAvantages = estCCQ ? (ccqExtra.ccq_avantages_sociaux || 0) : 0;
  const ccqRetraite = estCCQ ? salaireAvecVacances * ((ccqExtra.ccq_retraite_taux || 0) / 100) : 0;
  const ccqFnc = estCCQ ? salaireAvecVacances * ((ccqExtra.ccq_fnc_taux || 0) / 100) : 0;
  const ccqFormation = estCCQ ? salaireAvecVacances * ((ccqExtra.ccq_formation_taux || 0) / 100) : 0;

  const chargesSociales = rrq + ae + rqap + fss + cnesst + ccqAvantages + ccqRetraite + ccqFnc + ccqFormation;
  const total = salaireAvecVacances + chargesSociales + assurances_employeur_h + primeHoraire + bonusHoraire;

  // Temps et demi
  const salaireOT = tauxBase * 1.5 + tauxBase * 1.5 * (taux.taux_vacances / 100);
  const chargesSocialesOT = salaireOT * ((taux.rrq_taux + taux.ae_taux + taux.rqap_taux + taux.fss_taux + taux.cnesst_taux) / 100)
    + (estCCQ ? (ccqAvantages + salaireOT * ((ccqExtra.ccq_retraite_taux || 0) / 100)) : 0);
  const totalOT = salaireOT + chargesSocialesOT + assurances_employeur_h + primeHoraire + bonusHoraire;

  return {
    normal: total,
    ot: totalOT,
    assurances_employe_h,
    breakdown: { tauxBase, vacances, rrq, ae, rqap, fss, cnesst, assurances_employeur_h, assurances_employe_h, ccqAvantages, ccqRetraite, ccqFnc, ccqFormation, primeHoraire, bonusHoraire, chargesSociales, salaireAvecVacances },
  };
}

function Row({ label, value, unit = '$/h', highlight = false, indent = false, sub = false }) {
  return (
    <div className={`flex justify-between items-center py-0.5 ${indent ? 'pl-4' : ''} ${highlight ? 'border-t pt-2 mt-1' : ''}`}>
      <span className={`${sub ? 'text-[10px]' : 'text-xs'} ${highlight ? 'font-semibold text-slate-700' : 'text-slate-500'}`}>{label}</span>
      <span className={`${sub ? 'text-[10px]' : 'text-xs'} font-mono ${highlight ? 'font-bold text-slate-900' : 'text-slate-700'}`}>
        {typeof value === 'number' ? value.toFixed(3) : value} {unit}
      </span>
    </div>
  );
}

function TauxInput({ label, fieldKey, values, onChange, unit = '%', step = 0.01, fixedByLaw = false, lastVerified = null }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1">
        <Label className={`text-xs flex items-center gap-1.5 ${fixedByLaw ? 'text-slate-500' : 'text-slate-700'}`}>
          {fixedByLaw
            ? <Lock className="w-3 h-3 text-slate-400" />
            : <Unlock className="w-3 h-3 text-blue-400" />
          }
          {label}
          {fixedByLaw && <Badge className="bg-slate-100 text-slate-500 text-[9px] py-0 px-1">Fixé par loi</Badge>}
        </Label>
        {fixedByLaw && lastVerified && (
          <p className="text-[9px] text-slate-400 pl-4">Vérifié: {new Date(lastVerified).toLocaleDateString('fr-CA')}</p>
        )}
      </div>
      <div className="flex items-center gap-1 w-28">
        <Input
          type="number"
          step={step}
          value={values[fieldKey] ?? 0}
          onChange={e => !fixedByLaw && onChange({ ...values, [fieldKey]: parseFloat(e.target.value) || 0 })}
          className={`h-7 text-xs text-right ${fixedByLaw ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''}`}
          readOnly={fixedByLaw}
          disabled={fixedByLaw}
          data-testid={`calc-${fieldKey}`}
        />
        <span className="text-xs text-slate-400 w-5">{unit}</span>
      </div>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────
export function CalculatriceEmployeur({ salaires, onChange }) {
  const [showDetails, setShowDetails] = useState(false);
  const [showCCQDetails, setShowCCQDetails] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [officialRates, setOfficialRates] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const tauxBase = salaires.hors_decret || salaires.residentiel_leger || salaires.commercial_industriel || 0;
  const estCCQ = salaires.calculatrice?.est_ccq || false;
  const typePaiement = salaires.type_paiement || 'standard';
  const primeHoraire = salaires.prime_horaire || 0;
  const bonusType = salaires.bonus_type || 'horaire';
  
  // Bonus fixe: diviser par les heures ANNUELLES (pas par période de paie)
  // 2 saisons de ~26 semaines chacune
  const heuresEte = (salaires.heures_semaine_ete || 40) * 26;
  const heuresHiver = (salaires.heures_semaine_hiver || 35) * 26;
  const heuresAnnuelles = heuresEte + heuresHiver;
  // Le bonus fixe est par saison → 2 saisons par an
  const bonusAnnuelTotal = (salaires.bonus_montant_fixe || 0) * 2;
  const bonusHoraire = bonusType === 'horaire' 
    ? (salaires.bonus_horaire || 0) 
    : heuresAnnuelles > 0 ? bonusAnnuelTotal / heuresAnnuelles : 0;

  const [taux, setTaux] = useState({ ...TAUX_HORS_CCQ_DEFAULT, ...(salaires.calculatrice?.taux || {}) });
  const [ccqExtra, setCcqExtra] = useState({ ...TAUX_CCQ_EXTRA_DEFAULT, ...(salaires.calculatrice?.ccq_extra || {}) });
  const lastVerified = officialRates?.last_verified_at;

  // Charger les taux officiels depuis la DB
  useEffect(() => {
    axios.get(`${API_URL}/api/taux-gouvernementaux`)
      .then(r => r.status === 200 ? r.data : null)
      .then(data => {
        if (data?.taux) {
          setOfficialRates(data);
          // Mettre à jour les taux fixes par loi depuis la DB
          setTaux(prev => ({
            ...prev,
            rrq_taux: data.taux.rrq_taux?.valeur ?? prev.rrq_taux,
            ae_taux: data.taux.ae_taux?.valeur ?? prev.ae_taux,
            rqap_taux: data.taux.rqap_taux?.valeur ?? prev.rqap_taux,
            fss_taux: data.taux.fss_taux?.valeur ?? prev.fss_taux,
          }));
        }
      })
      .catch(() => {});
  }, []);

  const recalculate = useCallback(() => {
    const result = calculerCoutEmployeur({ tauxBase, taux, estCCQ, ccqExtra, primeHoraire, bonusHoraire, typePaiement });
    const newCout = parseFloat(result.normal.toFixed(4));
    const newCoutOt = parseFloat(result.ot.toFixed(4));
    // Éviter la boucle infinie: ne mettre à jour que si les valeurs changent
    if (newCout !== salaires.cout_employeur || newCoutOt !== salaires.cout_employeur_ot) {
      onChange({
        ...salaires,
        cout_employeur: newCout,
        cout_employeur_ot: newCoutOt,
        assurances_employe_h: parseFloat(result.assurances_employe_h.toFixed(4)),
        calculatrice: { ...(salaires.calculatrice || {}), est_ccq: estCCQ, taux, ccq_extra: ccqExtra },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tauxBase, taux, estCCQ, ccqExtra, primeHoraire, bonusHoraire, typePaiement]);

  useEffect(() => { recalculate(); }, [recalculate]);

  const result = calculerCoutEmployeur({ tauxBase, taux, estCCQ, ccqExtra, primeHoraire, bonusHoraire, typePaiement });
  const { breakdown: bd } = result;

  const handleRefreshRates = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/taux-gouvernementaux/refresh`, null);
      const data = res.data;
      if (res.status === 200) {
        if (data.changes_detected) {
          toast.success(`Taux mis à jour ! Confiance: ${data.confiance}`);
          setOfficialRates(data.taux);
          setTaux(prev => ({
            ...prev,
            rrq_taux: data.taux?.taux?.rrq_taux?.valeur ?? prev.rrq_taux,
            ae_taux: data.taux?.taux?.ae_taux?.valeur ?? prev.ae_taux,
            rqap_taux: data.taux?.taux?.rqap_taux?.valeur ?? prev.rqap_taux,
          }));
        } else {
          toast.success(`Taux vérifiés — aucun changement détecté (confiance: ${data.confiance})`);
          if (data.taux) setOfficialRates(data.taux);
        }
      } else {
        toast.error('Erreur lors de la vérification');
      }
    } catch {
      toast.error('Impossible de vérifier les taux');
    } finally {
      setRefreshing(false);
    }
  };

  const assurances_total = taux.assurances_total_par_periode || 0;
  const assurances_moitie = (assurances_total / 2).toFixed(2);

  return (
    <Card className="border-2 border-blue-100 bg-gradient-to-br from-white to-blue-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="w-4 h-4 text-blue-600" />
            Calculatrice Coût Employeur {officialRates?.annee || 2026}
          </CardTitle>
          <div className="flex items-center gap-2">
            {lastVerified && (
              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                <CheckCircle className="w-3 h-3 text-green-500" />
                Vérifié {new Date(lastVerified).toLocaleDateString('fr-CA')}
              </span>
            )}
            <Badge className={typePaiement === 'simple' ? 'bg-amber-100 text-amber-800' : estCCQ ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'}>
              {typePaiement === 'simple' ? 'Paiement simple' : estCCQ ? 'CCQ' : 'Hors-CCQ'}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Résultats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-blue-200 p-3 text-center shadow-sm">
            <p className="text-xs text-slate-500 mb-1">Coût employeur / h (normal)</p>
            <p className="text-2xl font-bold text-blue-700">{result.normal.toFixed(2)} $</p>
            <p className="text-[10px] text-slate-400">≤ {taux.heures_par_periode / (salaires.heures_semaine_ete || 40) || 2} sem (≤40h/sem)</p>
          </div>
          <div className="bg-white rounded-xl border border-orange-200 p-3 text-center shadow-sm">
            <p className="text-xs text-slate-500 mb-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />Temps et demi</p>
            <p className="text-2xl font-bold text-orange-600">{result.ot.toFixed(2)} $</p>
            <p className="text-[10px] text-slate-400">&gt;40h/sem — utilisé dans le calcul de marge</p>
          </div>
        </div>

        {/* Assurances collectives — split 50/50 */}
        <div className="border rounded-xl p-3 bg-white space-y-2">
          <p className="text-xs font-semibold text-slate-700">Assurances collectives (50% employeur / 50% employé)</p>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs text-slate-600 flex-1">Coût total assurances / période ($)</Label>
            <div className="flex items-center gap-1 w-28">
              <Input
                type="number"
                step="0.01"
                value={taux.assurances_total_par_periode || 0}
                onChange={e => setTaux(t => ({ ...t, assurances_total_par_periode: parseFloat(e.target.value) || 0 }))}
                className="h-7 text-xs text-right"
                data-testid="calc-assurances-total"
              />
              <span className="text-xs text-slate-400 w-4">$</span>
            </div>
          </div>
          {assurances_total > 0 && (
            <div className="grid grid-cols-2 gap-2 text-center text-xs pt-1 border-t">
              <div className="bg-blue-50 rounded p-1.5">
                <p className="text-slate-500">Employeur paie</p>
                <p className="font-bold text-blue-700">{assurances_moitie} $ / période</p>
                <p className="text-[10px] text-slate-400">({(parseFloat(assurances_moitie) / Math.max(taux.heures_par_periode, 1) * 100 / (tauxBase || 1)).toFixed(2)}% du salaire)</p>
              </div>
              <div className="bg-amber-50 rounded p-1.5">
                <p className="text-slate-500">Employé déduit</p>
                <p className="font-bold text-amber-700">{assurances_moitie} $ / période</p>
                <p className="text-[10px] text-slate-400">Déduit de la paie nette</p>
              </div>
            </div>
          )}
        </div>

        {/* Toggles */}
        <div className="space-y-3 border rounded-xl p-3 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Soumis à la CCQ</Label>
              <p className="text-xs text-slate-400">Active les cotisations CCQ supplémentaires</p>
            </div>
            <Switch checked={estCCQ} onCheckedChange={val => onChange({ ...salaires, calculatrice: { ...(salaires.calculatrice || {}), est_ccq: val } })} data-testid="toggle-ccq" />
          </div>
          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              <Label className="text-sm font-medium">Payé simplement (sans déductions sociales)</Label>
              <p className="text-xs text-slate-400">RRQ/AE/RQAP/FSS non calculés</p>
            </div>
            <Switch checked={typePaiement === 'simple'} onCheckedChange={val => onChange({ ...salaires, type_paiement: val ? 'simple' : 'standard' })} data-testid="toggle-simple" />
          </div>
        </div>

        {/* Taux configurables */}
        <div>
          <div className="flex items-center justify-between">
            <button type="button" className="flex-1 flex items-center justify-between text-sm font-medium text-slate-700 py-2 border-t" onClick={() => setShowDetails(o => !o)} data-testid="toggle-calc-details">
              <span>Taux de cotisations</span>
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={handleRefreshRates}
              disabled={refreshing}
              className="ml-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 border-t py-2"
              title="Vérifier si les taux gouvernementaux ont changé"
              data-testid="btn-refresh-taux"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Vérif...' : 'Vérifier'}
            </button>
          </div>

          {showDetails && (
            <div className="space-y-2 pt-2 pb-1 border rounded-xl p-3 bg-slate-50">
              <div className="flex items-start gap-1.5 mb-2 p-2 bg-slate-100 rounded-lg text-xs text-slate-500">
                <Lock className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Les taux <strong>fixés par loi</strong> sont mis à jour automatiquement. Les taux <Unlock className="w-3 h-3 inline" /> sont <strong>configurables</strong> par votre entreprise.</span>
              </div>
              {typePaiement !== 'simple' && (
                <>
                  <TauxInput label="RRQ de base (patronal)" fieldKey="rrq_taux" values={taux} onChange={setTaux} fixedByLaw lastVerified={lastVerified} />
                  <TauxInput label="Assurance-emploi (patronal)" fieldKey="ae_taux" values={taux} onChange={setTaux} fixedByLaw lastVerified={lastVerified} />
                  <TauxInput label="RQAP (patronal)" fieldKey="rqap_taux" values={taux} onChange={setTaux} step={0.001} fixedByLaw lastVerified={lastVerified} />
                  <TauxInput label="FSS (Fonds services santé)" fieldKey="fss_taux" values={taux} onChange={setTaux} />
                </>
              )}
              <TauxInput label="CNESST (votre taux)" fieldKey="cnesst_taux" values={taux} onChange={setTaux} />
              <TauxInput label="Indemnité de vacances" fieldKey="taux_vacances" values={taux} onChange={setTaux} />
              <div className="flex items-center justify-between gap-3 pt-1 border-t">
                <Label className="text-xs text-slate-600 flex-1 flex items-center gap-1"><Unlock className="w-3 h-3 text-blue-400" />Heures par période de paie</Label>
                <div className="flex items-center gap-1 w-28">
                  <Input type="number" step="1" value={taux.heures_par_periode} onChange={e => setTaux(t => ({ ...t, heures_par_periode: parseFloat(e.target.value) || 80 }))} className="h-7 text-xs text-right" data-testid="calc-heures-periode" />
                  <span className="text-xs text-slate-400 w-4">h</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CCQ */}
        {estCCQ && (
          <div>
            <button type="button" className="w-full flex items-center justify-between text-sm font-medium text-purple-700 py-2 border-t" onClick={() => setShowCCQDetails(o => !o)}>
              <span>Taux CCQ configurables</span>
              {showCCQDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showCCQDetails && (
              <div className="space-y-2 pt-2 pb-1 border rounded-xl p-3 bg-purple-50">
                <TauxInput label="Avantages sociaux CCQ ($/h)" fieldKey="ccq_avantages_sociaux" values={ccqExtra} onChange={setCcqExtra} unit="$/h" step={0.01} />
                <TauxInput label="Fonds de retraite CCQ" fieldKey="ccq_retraite_taux" values={ccqExtra} onChange={setCcqExtra} />
                <TauxInput label="Financement CCQ" fieldKey="ccq_fnc_taux" values={ccqExtra} onChange={setCcqExtra} step={0.001} />
                <TauxInput label="Formation CCQ" fieldKey="ccq_formation_taux" values={ccqExtra} onChange={setCcqExtra} />
              </div>
            )}
          </div>
        )}

        {/* Décomposition */}
        <div>
          <button type="button" className="w-full flex items-center justify-between text-sm font-medium text-slate-600 py-2 border-t" onClick={() => setShowBreakdown(o => !o)}>
            <span className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" />Décomposition détaillée</span>
            {showBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showBreakdown && (
            <div className="border rounded-xl p-3 bg-white text-xs space-y-0.5">
              <Row label="Taux horaire de base" value={bd.tauxBase} />
              <Row label={`Vacances (${taux.taux_vacances}%)`} value={bd.vacances} indent />
              {typePaiement !== 'simple' && (
                <>
                  <Row label={`RRQ ${taux.rrq_taux}%`} value={bd.rrq} indent />
                  <Row label={`AE ${taux.ae_taux}%`} value={bd.ae} indent />
                  <Row label={`RQAP ${taux.rqap_taux}%`} value={bd.rqap} indent />
                  <Row label={`FSS ${taux.fss_taux}%`} value={bd.fss} indent />
                </>
              )}
              <Row label={`CNESST ${taux.cnesst_taux}%`} value={bd.cnesst} indent />
              {bd.assurances_employeur_h > 0 && <Row label="Assurances (50% employeur / h)" value={bd.assurances_employeur_h} indent />}
              {bd.assurances_employe_h > 0 && <Row label="Assurances (50% employé / h) — déduit de la paie nette" value={bd.assurances_employe_h} indent sub />}
              {estCCQ && bd.ccqAvantages > 0 && <Row label="Avantages sociaux CCQ" value={bd.ccqAvantages} indent />}
              {estCCQ && bd.ccqRetraite > 0 && <Row label="Retraite CCQ" value={bd.ccqRetraite} indent />}
              {bd.primeHoraire > 0 && <Row label="Prime horaire ($/h)" value={bd.primeHoraire} indent />}
              {bd.bonusHoraire > 0 && <Row label={`Bonus (${bonusType === 'fixe' ? `${salaires.bonus_montant_fixe}$/saison × 2 ÷ ${heuresAnnuelles}h` : '$/h'})`} value={bd.bonusHoraire} indent />}
              <Row label="COÛT EMPLOYEUR / h (normal ≤40h)" value={result.normal} unit="$/h" highlight />
              <Row label="COÛT EMPLOYEUR / h (temps et demi >40h)" value={result.ot} unit="$/h" highlight />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
